import * as yup from "yup";
import {
  AtimInterface,
  DataModelVersion,
  HitchType,
  PtoSpeed,
  PtoType,
} from "protos/atk/enums";
import { FormikProps } from "formik";
import { getInputGuid } from "portal/utils/ids";
import {
  Implement,
  ImplementArchetype,
  ScvInputs,
} from "protos/atk/autotractor";
import {
  implementArchetypeBoundaryViewModel,
  implementBoundaryViewModel,
  ImplementBoundaryViewModel,
  persistImplementBoundaryViewModel,
} from "../boundaryModel";
import { Polygon2D } from "protos/geometry/geometry";
import { polygon2DSchema, scvInputsSchema } from "../../schema";
import { setAndTouch } from "portal/utils/forms";
import {
  turningRadiusLimitsToViewModel,
  TurningRadiusLimitsViewModel,
} from "portal/utils/atkEquipment";
import { typedPath } from "typed-path";

export interface ImplementViewModel
  extends Omit<
    Implement,
    | "workingStartOffsetM"
    | "workingEndOffsetM"
    | "workingWidthM"
    | "boundary"
    | "boundaryModel"
    | "turningRadiusLimits"
    | "toolSpeedMS"
    | "transitionSpeedMS"
    | "runUpDistanceM"
    | "straightenDistanceM"
  > {
  turningRadiusLimits: TurningRadiusLimitsViewModel;
  toolSpeedMS: number;
  transitionSpeedMS: number;
  runUpDistanceM: number;
  straightenDistanceM: number;
  overrideAutonomyMeasurements: boolean;
  workingStartOffsetM: number;
  workingEndOffsetM: number;
  workingWidthM: number;
  // the existing boundary on the tractor object, in case the boundary model
  // can't be manipulated by Ops Center, so it can be written back unchanged
  existingBoundaryPolygon: Polygon2D | undefined;
  // the presence of the boundary model in the written-back implement is currently
  // governed by overrideAutonomyMeasurements - cqb
  boundaryViewModel: ImplementBoundaryViewModel;
}

export const ptoRequirementsSchemaImplement = yup.object({
  type: yup
    .number()
    .oneOf(
      [
        PtoType.PTO_TYPE_FRONT,
        PtoType.PTO_TYPE_FRONT_AND_REAR,
        PtoType.PTO_TYPE_REAR,
      ],
      "utils.form.required"
    )
    .required("utils.form.required"),
  engineSpeedRpm: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  ptoSpeed: yup
    .number()
    .oneOf(
      [
        PtoSpeed.PTO_SPEED_540E,
        PtoSpeed.PTO_SPEED_540,
        PtoSpeed.PTO_SPEED_1000E,
        PtoSpeed.PTO_SPEED_1000,
      ],
      "utils.form.required"
    )
    .required("utils.form.required"),
});

export const viewModelSchema = yup.object({
  name: yup.string().required(),
  hitchType: yup
    .number()
    .oneOf(
      [
        HitchType.HITCH_TYPE_DRAW_BAR_REAR,
        HitchType.HITCH_TYPE_THREE_POINT_FRONT,
        HitchType.HITCH_TYPE_THREE_POINT_REAR,
      ],
      "utils.form.required"
    )
    .optional(),
  atim: yup
    .number()
    .oneOf([
      AtimInterface.ATIM_GENERIC,
      AtimInterface.ATIM_LASER_WEEDER,
      AtimInterface.ATIM_NONE,
    ])
    .optional(),
  workingWidthM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  workingStartOffsetM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  workingEndOffsetM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  toolSpeedMS: yup.number().min(0, "utils.form.greaterOrEqualZero").optional(),
  transitionSpeedMS: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  runUpDistanceM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  straightenDistanceM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .optional(),
  boundary: polygon2DSchema.optional(),
  ptoRequirements: ptoRequirementsSchemaImplement
    .notRequired()
    .default(undefined),
  scvInputs: scvInputsSchema.optional(),

  // turning radius
  turningRadiusLimits: yup.object<TurningRadiusLimitsViewModel>({
    radius75to100M: yup.number().positive(),
    radius50to75M: yup
      .number()
      .min(
        yup.ref("radius75to100M"),
        "views.autotractor.equipment.implements.turningRadius.error50_75lt75_100"
      ),
    radius25to50M: yup
      .number()
      .min(
        yup.ref("radius50to75M"),
        "views.autotractor.equipment.implements.turningRadius.error25_50lt50_75"
      ),
    radius0to25M: yup.number().min(yup.ref("radius25to50M")),
  }),
});

/**
 * this isn't used anywhere -- it just ensures that the schema and the
 * shape of the type it describes don't drift apart at typecheck time.
 */
implementToViewModel(undefined) satisfies yup.InferType<typeof viewModelSchema>;

const S = typedPath<ImplementViewModel>();

/**
 * changing the archetype for an implement can overwrite fields in the view
 * model.  handle this.
 */
export function changeArchetype(
  formik: FormikProps<ImplementViewModel>,
  archetype: ImplementArchetype
): void {
  setAndTouch(formik, S.archetype, archetype);

  // note: not all of this flow's state management has moved over to the view
  // model yet.  this function will grow as it does.
  if (!formik.values.overrideAutonomyMeasurements) {
    setAndTouch(formik, S.toolSpeedMS, archetype.toolSpeedMS);
    setAndTouch(formik, S.transitionSpeedMS, archetype.transitionSpeedMS);
    setAndTouch(formik, S.runUpDistanceM, archetype.runUpDistanceM);
    setAndTouch(formik, S.straightenDistanceM, archetype.straightenDistanceM);
    setAndTouch(
      formik,
      S.turningRadiusLimits,
      turningRadiusLimitsToViewModel(archetype.turningRadiusLimits)
    );
    setAndTouch(
      formik,
      S.boundaryViewModel,
      implementArchetypeBoundaryViewModel(archetype)
    );
    setAndTouch(formik, S.workingWidthM, archetype.workingWidthM);
    setAndTouch(formik, S.workingStartOffsetM, archetype.workingStartOffsetM);
    setAndTouch(formik, S.workingEndOffsetM, archetype.workingEndOffsetM);
  }
}

export function implementToViewModel(
  existingImplement: Implement | undefined
): ImplementViewModel {
  const baseImplement = existingImplement ?? Implement.fromPartial({});

  const boundaryViewModel = implementBoundaryViewModel(baseImplement);

  const overrideAutonomyMeasurements =
    baseImplement.workingWidthM !== undefined ||
    baseImplement.workingStartOffsetM !== undefined ||
    baseImplement.workingEndOffsetM !== undefined ||
    baseImplement.toolSpeedMS !== undefined ||
    baseImplement.transitionSpeedMS !== undefined ||
    baseImplement.runUpDistanceM !== undefined ||
    baseImplement.straightenDistanceM !== undefined ||
    baseImplement.turningRadiusLimits.length > 0 ||
    boundaryViewModel.present;

  let turningRadiusLimits: TurningRadiusLimitsViewModel = {
    radius0to25M: 0,
    radius25to50M: 0,
    radius50to75M: 0,
    radius75to100M: 0,
  };

  if (baseImplement.turningRadiusLimits.length > 0) {
    turningRadiusLimits = turningRadiusLimitsToViewModel(
      baseImplement.turningRadiusLimits
    );
  } else if (baseImplement.archetype) {
    turningRadiusLimits = turningRadiusLimitsToViewModel(
      baseImplement.archetype.turningRadiusLimits
    );
  }

  return {
    ...baseImplement,
    overrideAutonomyMeasurements,
    workingWidthM:
      baseImplement.workingWidthM ??
      baseImplement.archetype?.workingWidthM ??
      0,
    workingStartOffsetM:
      baseImplement.workingStartOffsetM ??
      baseImplement.archetype?.workingStartOffsetM ??
      0,
    workingEndOffsetM:
      baseImplement.workingEndOffsetM ??
      baseImplement.archetype?.workingEndOffsetM ??
      0,
    existingBoundaryPolygon: baseImplement.boundary,
    boundaryViewModel: boundaryViewModel.value,
    toolSpeedMS:
      baseImplement.toolSpeedMS ?? baseImplement.archetype?.toolSpeedMS ?? 0,
    transitionSpeedMS:
      baseImplement.transitionSpeedMS ??
      baseImplement.archetype?.transitionSpeedMS ??
      0,
    runUpDistanceM:
      baseImplement.runUpDistanceM ??
      baseImplement.archetype?.runUpDistanceM ??
      0,
    straightenDistanceM:
      baseImplement.straightenDistanceM ??
      baseImplement.archetype?.straightenDistanceM ??
      0,
    turningRadiusLimits,
  };
}

export function viewModelToImplement(viewModel: ImplementViewModel): Implement {
  let scvInputs: ScvInputs | undefined;

  // Adds GUIDs to scvInputs if they were missing; this fixes a bug with old
  // broken implement definitions.
  if (viewModel.scvInputs) {
    scvInputs = {
      inputs: viewModel.scvInputs.inputs.map((i) => {
        const newInput = { ...i };
        if (!i.id) {
          newInput.id = getInputGuid();
        }
        return i;
      }),
    };
  }

  let overriddenAutonomyMeasurements: Pick<Implement, "turningRadiusLimits"> &
    Partial<
      Pick<
        Implement,
        | "workingWidthM"
        | "workingStartOffsetM"
        | "workingEndOffsetM"
        | "toolSpeedMS"
        | "transitionSpeedMS"
        | "runUpDistanceM"
        | "straightenDistanceM"
        | "boundary"
        | "boundaryModel"
      >
    >;

  if (viewModel.overrideAutonomyMeasurements) {
    overriddenAutonomyMeasurements = {
      ...persistImplementBoundaryViewModel(
        viewModel.boundaryViewModel,
        viewModel.existingBoundaryPolygon,
        viewModel.workingWidthM / 2,
        viewModel.workingWidthM / 2,
        viewModel.workingStartOffsetM
      ),
      toolSpeedMS: viewModel.toolSpeedMS,
      transitionSpeedMS: viewModel.transitionSpeedMS,
      runUpDistanceM: viewModel.runUpDistanceM,
      straightenDistanceM: viewModel.straightenDistanceM,
      turningRadiusLimits: [
        {
          maxHeight: 25,
          minTurningRadiusM: viewModel.turningRadiusLimits.radius0to25M,
        },
        {
          maxHeight: 50,
          minTurningRadiusM: viewModel.turningRadiusLimits.radius25to50M,
        },
        {
          maxHeight: 75,
          minTurningRadiusM: viewModel.turningRadiusLimits.radius50to75M,
        },
        {
          maxHeight: 100,
          minTurningRadiusM: viewModel.turningRadiusLimits.radius75to100M,
        },
      ],
    };
  } else {
    overriddenAutonomyMeasurements = {
      workingWidthM: undefined,
      workingStartOffsetM: undefined,
      workingEndOffsetM: undefined,
      toolSpeedMS: undefined,
      transitionSpeedMS: undefined,
      runUpDistanceM: undefined,
      straightenDistanceM: undefined,
      turningRadiusLimits: [],
      boundary: viewModel.existingBoundaryPolygon,
      boundaryModel: undefined,
    };
  }

  return {
    ...viewModel,
    ...overriddenAutonomyMeasurements,
    scvInputs,
  };
}

export function editingSupported(viewModel: ImplementViewModel): boolean {
  return (
    viewModel.dataModelVersion < DataModelVersion.DATA_MODEL_VERSION_M6_BASE
  );
}
